import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { CognitoUser, AuthenticationDetails, CognitoUserPool } from 'amazon-cognito-identity-js';
import { translateCognitoError } from '../utils/errorTranslation';

// Cognito Configuration by Environment
const getCognitoConfig = () => {
  const environment = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';
  const hostname = window.location.hostname;
  
  // Force production Cognito for localhost when REACT_APP_ENV=production
  if (process.env.REACT_APP_ENV === 'production' && hostname === 'localhost') {
    return {
      UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID_PROD || 'us-west-2_6NaygtqLh',
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID_PROD || '240r8kacjngq3bgdlpq9veabs8'
    };
  }
  
  // Production domains (custom domains)
  const productionDomains = [
    'salustrack.com',
    'app.salustrack.com', 
    'dashboard.salustrack.com',
    'main.dxe47duupmdwq.amplifyapp.com' // Amplify URL
  ];
  
  // Development domains
  const developmentDomains = [
    'develop.dxe47duupmdwq.amplifyapp.com',
    'dev.salustrack.com',
    'localhost'
  ];
  
  // Staging domains
  const stagingDomains = [
    'staging.dxe47duupmdwq.amplifyapp.com',
    'staging.salustrack.com'
  ];
  
  // Determine environment
  if (productionDomains.some(domain => hostname.includes(domain)) || environment === 'production') {
    return {
      UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID_PROD || 'us-west-2_6NaygtqLh',
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID_PROD || '240r8kacjngq3bgdlpq9veabs8'
    };
  } else if (developmentDomains.some(domain => hostname.includes(domain)) || environment === 'development') {
    return {
      UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID_DEV || 'us-west-2_6NaygtqLh',
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID_DEV || '240r8kacjngq3bgdlpq9veabs8'
    };
  } else if (stagingDomains.some(domain => hostname.includes(domain)) || environment === 'staging') {
    return {
      UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID_STAGING || 'us-west-2_6NaygtqLh',
      ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID_STAGING || '240r8kacjngq3bgdlpq9veabs8'
    };
  }
  
  // Default to prod
  return {
    UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID_PROD || 'us-west-2_6NaygtqLh',
    ClientId: process.env.REACT_APP_COGNITO_CLIENT_ID_PROD || '240r8kacjngq3bgdlpq9veabs8'
  };
};

const poolData = getCognitoConfig();
const userPool = new CognitoUserPool(poolData);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [t, setT] = useState(() => (key) => key); // Função de tradução padrão

  useEffect(() => {
    const loadUser = () => {
      const currentUser = userPool.getCurrentUser();
      if (currentUser) {
        currentUser.getSession((err, session) => {
          if (err || !session.isValid()) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('id_token');
            setLoading(false);
            return;
          }
          
          const idToken = session.getIdToken();
          const idPayload = idToken.payload;
          
          // Store tokens
          localStorage.setItem('id_token', idToken.getJwtToken());
          localStorage.setItem('auth_token', session.getAccessToken().getJwtToken());
          
          const userData = {
            id: idPayload.sub,
            email: idPayload.email,
            name: idPayload.name || idPayload.given_name || 'Super Admin',
            role: idPayload['custom:role'] || 'user',
            tenant_id: idPayload['custom:tenant_id'] || null,
            permissions: idPayload['custom:permissions'] ? JSON.parse(idPayload['custom:permissions']) : []
          };
          
          setUser(userData);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    };
    
    loadUser();
    
    // Auto-refresh token every 45 minutes (tokens expire in 60 minutes)
    const refreshInterval = setInterval(() => {
      const currentUser = userPool.getCurrentUser();
      if (currentUser) {
        currentUser.getSession((err, session) => {
          if (err || !session.isValid()) {
            console.log('Session refresh failed:', err);
            logout();
            return;
          }
          
          const now = Math.floor(Date.now() / 1000);
          const tokenExp = session.getIdToken().payload.exp;
          
          if (tokenExp <= now + 300) {
            console.log('Token will expire soon, forcing re-login');
            logout();
            return;
          }
          
          localStorage.setItem('id_token', session.getIdToken().getJwtToken());
          localStorage.setItem('auth_token', session.getAccessToken().getJwtToken());
          console.log('Session refreshed successfully');
        });
      }
    }, 45 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password) => {
    return new Promise((resolve) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: email,
        Password: password
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          const accessToken = result.getAccessToken().getJwtToken();
          const idToken = result.getIdToken().getJwtToken();
          const idPayload = result.getIdToken().payload;
          
          const userData = {
            id: idPayload.sub,
            email: idPayload.email,
            name: idPayload.name || idPayload.given_name || 'Super Admin',
            role: idPayload['custom:role'] || 'user',
            tenant_id: idPayload['custom:tenant_id'] || null,
            permissions: idPayload['custom:permissions'] ? JSON.parse(idPayload['custom:permissions']) : []
          };
          
          localStorage.setItem('auth_token', accessToken);
          localStorage.setItem('id_token', idToken);
          setUser(userData);
          resolve({ success: true });
        },
        onFailure: (err) => {
          console.error('Login error:', err);
          let errorMessage = 'Erro ao fazer login';
          
          if (err.code === 'NotAuthorizedException') {
            errorMessage = 'Email ou senha incorretos';
          } else if (err.code === 'UserNotFoundException') {
            errorMessage = 'Usuário não encontrado';
          } else if (err.code === 'UserNotConfirmedException') {
            errorMessage = 'Usuário não confirmado';
          } else if (err.code === 'TooManyRequestsException') {
            errorMessage = 'Muitas tentativas. Tente novamente mais tarde';
          } else if (err.message) {
            errorMessage = translateCognitoError(err.message, t);
          }
          
          resolve({ 
            success: false, 
            error: errorMessage
          });
        },
        newPasswordRequired: (userAttributes, requiredAttributes) => {
          resolve({
            success: false,
            needsNewPassword: true,
            cognitoUser,
            userAttributes,
            requiredAttributes,
            error: 'Nova senha necessária. Entre em contato com o administrador.'
          });
        }
      });
    });
  };

  const forgotPassword = async (email) => {
    return new Promise((resolve) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve({ 
            success: true, 
            message: 'Código de verificação enviado para seu email' 
          });
        },
        onFailure: (err) => {
          const translatedError = translateCognitoError(err.message, t);
          resolve({ 
            success: false, 
            error: translatedError
          });
        }
      });
    });
  };

  const confirmPassword = async (email, verificationCode, newPassword) => {
    return new Promise((resolve) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool
      });

      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          resolve({ 
            success: true, 
            message: 'Senha alterada com sucesso' 
          });
        },
        onFailure: (err) => {
          const translatedError = translateCognitoError(err.message, t);
          resolve({ 
            success: false, 
            error: translatedError
          });
        }
      });
    });
  };

  const completeNewPassword = async (cognitoUser, newPassword, userAttributes = {}) => {
    return new Promise((resolve) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
        onSuccess: (result) => {
          const accessToken = result.getAccessToken().getJwtToken();
          const idToken = result.getIdToken().getJwtToken();
          const idPayload = result.getIdToken().payload;
          
          const userData = {
            id: idPayload.sub,
            email: idPayload.email,
            name: idPayload.name || idPayload.given_name || 'Super Admin',
            role: idPayload['custom:role'] || 'user',
            tenant_id: idPayload['custom:tenant_id'] || null,
            permissions: idPayload['custom:permissions'] ? JSON.parse(idPayload['custom:permissions']) : []
          };
          
          localStorage.setItem('auth_token', accessToken);
          localStorage.setItem('id_token', idToken);
          setUser(userData);
          resolve({ success: true });
        },
        onFailure: (err) => {
          const translatedError = translateCognitoError(err.message, t);
          resolve({ 
            success: false, 
            error: translatedError
          });
        }
      });
    });
  };

  const refreshSession = useCallback(async () => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      return new Promise((resolve) => {
        currentUser.getSession((err, session) => {
          if (err || !session.isValid()) {
            console.log('Session refresh failed:', err);
            logout();
            resolve(false);
            return;
          }
          
          // Check if token is actually valid and not expired
          const now = Math.floor(Date.now() / 1000);
          const tokenExp = session.getIdToken().payload.exp;
          
          if (tokenExp <= now + 300) { // If expires in less than 5 minutes
            console.log('Token will expire soon, forcing re-login');
            logout();
            resolve(false);
            return;
          }
          
          // Update tokens
          localStorage.setItem('id_token', session.getIdToken().getJwtToken());
          localStorage.setItem('auth_token', session.getAccessToken().getJwtToken());
          console.log('Session refreshed successfully');
          resolve(true);
        });
      });
    }
    return false;
  }, []);

  const logout = () => {
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
    localStorage.removeItem('auth_token');
    localStorage.removeItem('id_token');
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    refreshSession,
    forgotPassword,
    confirmPassword,
    completeNewPassword,
    isAuthenticated: !!user,
    tenantId: user?.tenant_id,
    tenantName: user?.tenant_name,
    isSuperAdmin: user?.role === 'super_admin' || false,
    userRole: user?.role,
    setTranslationFunction: setT
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};